import { IonButton, IonButtons, IonCardContent, IonCol, IonGrid, IonIcon, IonItem, IonLabel, IonRow, IonText } from '@ionic/react';
import { IRepairOrder } from '../../../models/dms/repairOrder.model';
import { IonListWrapper } from './FixedAssetDetails.styles';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import store from '../../../store';
import { fetchFixedAssetDetailsStoreAsync, setSelectedFixedAssetStoreAsync } from '../../../store/fixedAssetSlice';
import { IFixedAssetDetails } from '../../../models/dms/fixedAsset.model';
import { unwrapResult } from '@reduxjs/toolkit';
import { setLabourClockingRepairOrderStoreAsync } from '../../../store/technicianClockingSlice';
import { ILabourClocking } from '../../../models/dms/clocking.model';
import { AttachmentAssetType, IAttachmentContainer } from '../../../models/dms/attachment.model';
import { refresh } from 'ionicons/icons';
import { setSelectedVehicleAttachmentsStoreAsync } from '../../../store/vehicleAttachmentSlice';
import SelectedVehicleAttachments from '../../selectedVehicleAttachments/SelectedVehicleAttachments';

interface IComponentProps {
  repairOrder: IRepairOrder;
}

const FixedAssetDetails: React.FC<IComponentProps> = ({ repairOrder }) => {
  const [loadingError, setLoadingError] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const { selectedFixedAsset, selectedFixedAssetLoading } = useAppSelector((state) => state.fixedAsset);
  const { networkConnected } = useAppSelector((state) => state.app);

  const dispatch = useAppDispatch();

  useEffect(() => {
    loadRepairOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRepairOrder = async () => {
    const { selectedFixedAsset } = store.getState().fixedAsset;
    const { selectedVehicleAttachments } = store.getState().vehicleAttachments;

    // There is a chance that the unit id, vehicle (and other details) will be null. This happens when the RO is clocked on
    // automatically from the server or from clocking history and no info is available in the cache to fill this information.

    await dispatch(
      setSelectedVehicleAttachmentsStoreAsync({
        assetType: AttachmentAssetType.FixedAsset,
        key: repairOrder.vehicle.sourceRef,
        jobCode: '',
        jobType: '',
        repairOrderNumber: repairOrder.repairOrderNumber?.toString() || '-1',
        mediaRef: '',
        attachments: selectedVehicleAttachments?.key === repairOrder.vehicle.sourceRef ? [...selectedVehicleAttachments.attachments] : [],
      } as IAttachmentContainer),
    );

    if (repairOrder.vehicle.sourceRef) {
      // Unit id is available
      if (repairOrder.vehicle.sourceRef !== selectedFixedAsset?.unitId) {
        // Reset vehicle details to default
        dispatch(
          setSelectedFixedAssetStoreAsync({
            unitId: repairOrder.vehicle.sourceRef,
          } as IFixedAssetDetails),
        );
        // Load workshop vehicle
        await loadFixedAsset(repairOrder.vehicle.sourceRef, repairOrder.repairOrderNumber || -1);
      }
    } else {
      // Stock number (and other values) are unavailable. Load the repair order to get the stock number
      try {
        if (repairOrder.repairOrderNumber) {
          setLoadingData(true);
          const resultAction = await dispatch(setLabourClockingRepairOrderStoreAsync(repairOrder.repairOrderNumber));
          const result = unwrapResult(resultAction) as ILabourClocking;
          loadFixedAsset(result.repairOrder.vehicle.sourceRef || '', repairOrder.repairOrderNumber);
        }
      } catch (ex) {
        setLoadingError(true);
      } finally {
        setLoadingData(false);
      }
    }
  };

  const loadFixedAsset = async (unitId: string, repairOrderNumber: number) => {
    try {
      setLoadingData(true);
      const resultAction = await dispatch(fetchFixedAssetDetailsStoreAsync({ unitId, repairOrderNumber }));
      unwrapResult(resultAction);
      setLoadingError(false);
    } catch (ex) {
      setLoadingError(true);
    } finally {
      setLoadingData(false);
    }
  };

  const handleClickLoadRepairOrderDetails = async () => {
    await loadFixedAsset(repairOrder.vehicle.sourceRef || '', repairOrder.repairOrderNumber || -1);
  };

  return (
    <IonListWrapper>
      {/* Header */}
      <IonItem>
        <IonButtons slot="end">
          <IonButton onClick={() => handleClickLoadRepairOrderDetails()} fill="clear" disabled={selectedFixedAssetLoading || loadingData}>
            <IonIcon icon={refresh} slot="icon-only" color="primary" />
          </IonButton>
        </IonButtons>
        Fixed Asset Details
      </IonItem>

      {/* Data */}
      <IonItem lines="none">
        <IonCardContent>
          <IonGrid>
            <IonRow>
              <IonCol size="auto">
                <IonItem>
                  <IonLabel position="stacked">Fixed Asset</IonLabel>
                  <IonText class="flexed flexed-center">{repairOrder?.vehicle?.sourceRef || ''}</IonText>
                </IonItem>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonCardContent>
      </IonItem>
      {selectedFixedAsset?.unitId && repairOrder.repairOrderNumber && <SelectedVehicleAttachments />}
      {loadingError && (
        <IonItem lines="none" disabled={selectedFixedAssetLoading || loadingData}>
          <IonText color="danger">{networkConnected && <h4>Failed to load fixed asset details</h4>}</IonText>
        </IonItem>
      )}
    </IonListWrapper>
  );
};

export default FixedAssetDetails;
